import React from 'react';
import { Lock, AlertTriangle, CheckCircle2, Clock, Ban } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatusCategory = 'cycle' | 'settlement' | 'issue' | 'severity' | 'void';

interface StatusBadgeProps {
  status: string;
  category?: StatusCategory;
  className?: string;
  showIcon?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  category = 'cycle',
  className,
  showIcon = true,
}) => {
  const normalized = status.toLowerCase();

  // 1. Audit Voided
  if (category === 'void' || normalized === 'void' || normalized === 'voided') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300 line-through',
          className
        )}
      >
        {showIcon && <Ban className="w-3 h-3 text-red-600" />}
        VOIDED
      </span>
    );
  }

  // 2. Production Cycle Status
  if (category === 'cycle') {
    switch (normalized) {
      case 'active':
      case 'selling':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300',
              className
            )}
          >
            {showIcon && <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />}
            Active
          </span>
        );
      case 'draft':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200',
              className
            )}
          >
            {showIcon && <Clock className="w-3 h-3 text-amber-600" />}
            Draft
          </span>
        );
      case 'finalized':
      case 'settled':
      case 'frozen':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-300',
              className
            )}
          >
            {showIcon && <CheckCircle2 className="w-3 h-3 text-indigo-700" />}
            Finalized
          </span>
        );
      case 'closed':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-300',
              className
            )}
          >
            {showIcon && <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />}
            Closed
          </span>
        );
      default:
        return (
          <span className={cn('px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700', className)}>
            {status}
          </span>
        );
    }
  }

  // 3. Settlement Status
  if (category === 'settlement') {
    switch (normalized) {
      case 'active':
      case 'ready_to_settle':
      case 'unstarted':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300',
              className
            )}
          >
            {showIcon && <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />}
            Active
          </span>
        );
      case 'draft':
      case 'approved':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200',
              className
            )}
          >
            {showIcon && <Clock className="w-3 h-3 text-amber-600" />}
            Draft
          </span>
        );
      case 'finalized':
      case 'frozen':
      case 'settled':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-900 border border-indigo-400',
              className
            )}
          >
            {showIcon && <Lock className="w-3 h-3 text-indigo-700" />}
            Finalized
          </span>
        );
      case 'closed':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-300',
              className
            )}
          >
            {showIcon && <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />}
            Closed
          </span>
        );
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100">{status}</span>;
    }
  }

  // 4. Review & Resolve Issue Status
  if (category === 'issue') {
    switch (normalized) {
      case 'needs_review':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300',
              className
            )}
          >
            {showIcon && <AlertTriangle className="w-3 h-3 text-amber-700" />}
            Needs Review
          </span>
        );
      case 'resolved':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-900 border border-emerald-300',
              className
            )}
          >
            {showIcon && <CheckCircle2 className="w-3 h-3 text-emerald-700" />}
            Resolved
          </span>
        );
      case 'historical_incomplete_accepted':
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-300',
              className
            )}
          >
            Historical Accepted
          </span>
        );
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100">{status}</span>;
    }
  }

  // 5. Severity
  if (category === 'severity') {
    switch (normalized) {
      case 'critical':
        return (
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300', className)}>
            Critical
          </span>
        );
      case 'warning':
        return (
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300', className)}>
            Warning
          </span>
        );
      case 'info':
        return (
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-300', className)}>
            Info
          </span>
        );
    }
  }

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800', className)}>
      {status}
    </span>
  );
};
